import { useGetMySupplierMembersLazyQuery } from "@repo/server-api";
import { useDebounce } from "@repo/shared/common";
import { useNotification } from "@repo/shared/components";
import { useCallback, useEffect, useState } from "react";

import type { PaginationModel } from "../types/UserTypes";
import type { SupplierMember } from "../types/UserTypes";

export type UseSupplierMembersListReturn = {
	members: SupplierMember[];
	rowCount: number;
	isLoading: boolean;

	searchValue: string;
	setSearchValue: (value: string) => void;

	paginationModel: PaginationModel;
	setPaginationModel: (model: PaginationModel) => void;

	refresh: () => void;
};

export function useSupplierMembersList(): UseSupplierMembersListReturn {
	const { showNotification } = useNotification();

	const [members, setMembers] = useState<SupplierMember[]>([]);
	const [rowCount, setRowCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);

	const [searchValue, setSearchValue] = useState("");
	const [paginationModel, setPaginationModel] = useState<PaginationModel>({
		page: 0,
		pageSize: 10,
	});

	const debouncedSearchValue = useDebounce(searchValue, 300);

	const [getSupplierMembers] = useGetMySupplierMembersLazyQuery({
		fetchPolicy: "no-cache",
		onError(error: { message: string }) {
			showNotification(error.message, "error");
			setIsLoading(false);
		},
	});

	const fetchData = useCallback(async () => {
		setIsLoading(true);

		const response = await getSupplierMembers({
			variables: {
				first: paginationModel.pageSize,
				after: (paginationModel.pageSize * paginationModel.page).toString(),
				filterByName: debouncedSearchValue || null,
			},
		});

		if (response.data?.mySupplierMembers?.nodes) {
			const mapped: SupplierMember[] = response.data.mySupplierMembers.nodes.map((m) => ({
				id: m.id,
				displayName: m.displayName,
			}));
			setMembers(mapped);
			setRowCount(response.data.mySupplierMembers.totalCount);
		}

		setIsLoading(false);
	}, [getSupplierMembers, paginationModel, debouncedSearchValue]);

	const refresh = useCallback(() => {
		fetchData();
	}, [fetchData]);

	useEffect(() => {
		fetchData();
	}, [paginationModel, debouncedSearchValue]);

	return {
		members,
		rowCount,
		isLoading,

		searchValue,
		setSearchValue,

		paginationModel,
		setPaginationModel,

		refresh,
	};
}
